import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, signal, untracked, ChangeDetectionStrategy } from '@angular/core';
import { Api } from '../../../api/api';
import { getLocationTariffs } from '../../../api/functions';
import { LocationTariffBandView } from '../../../api/models/location-tariff-band-view';
import { LocationTariffView } from '../../../api/models/location-tariff-view';
import { formatApiError } from '../../../common/utils/api-error';

type Phase = 'idle' | 'loading' | 'error';
type PriceType = LocationTariffView['priceType'];
type Direction = LocationTariffView['direction'];

/** Zeitzone, in der Bänder und Gültigkeiten aufgelöst werden (wie `LocationPriceServiceImpl`). */
const TARIFF_ZONE = 'Europe/Vienna';

/** Die vier Dimensionen eines Standorts, in der Reihenfolge der Preisformeln. */
const DIMENSIONS: readonly { priceType: PriceType; direction: Direction; label: string }[] = [
  { priceType: 'ENERGY', direction: 'CONSUMPTION', label: 'Energiepreis Bezug' },
  { priceType: 'GRID', direction: 'CONSUMPTION', label: 'Netzpreis Bezug' },
  { priceType: 'ENERGY', direction: 'FEED_IN', label: 'Energiepreis Einspeisung' },
  { priceType: 'GRID', direction: 'FEED_IN', label: 'Netzpreis Einspeisung' },
];

type Status = 'current' | 'future' | 'past';

interface TariffCard {
  tariff: LocationTariffView;
  status: Status;
}

interface DimensionGroup {
  label: string;
  key: string;
  cards: TariffCard[];
}

/**
 * Die Zeilen von `location_tariff` und `location_tariff_band` des Standorts, nur lesend, Feld für Feld beschriftet.
 */
@Component({
  selector: 'app-location-tariffs',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './location-tariffs.component.html',
})
export class LocationTariffsComponent {
  private readonly api = inject(Api);

  readonly locationId = input.required<string>();

  readonly tariffs = signal<LocationTariffView[] | null>(null);
  readonly phase = signal<Phase>('idle');
  readonly message = signal<string | null>(null);

  readonly currentCount = computed(() => (this.tariffs() ?? []).filter((tariff) => tariff.current).length);

  readonly groups = computed<DimensionGroup[]>(() => {
    const tariffs = this.tariffs() ?? [];
    return DIMENSIONS.map((dimension) => ({
      label: dimension.label,
      key: `${dimension.priceType}/${dimension.direction}`,
      cards: tariffs
        .filter((tariff) => tariff.priceType === dimension.priceType && tariff.direction === dimension.direction)
        .map((tariff) => toCard(tariff)),
    }));
  });

  constructor() {
    effect(() => {
      const locationId = this.locationId();
      if (locationId) {
        untracked(() => this.load(locationId));
      }
    });
  }

  formatInstant(value: string | undefined): string {
    if (!value) {
      return 'offen';
    }
    return new Date(value).toLocaleString('de-AT', {
      timeZone: TARIFF_ZONE,
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  }

  componentEntries(band: LocationTariffBandView): { name: string; value: number }[] {
    return Object.entries(band.components ?? {}).map(([name, value]) => ({ name, value }));
  }

  unitLabel(unit: string): string {
    return unitLabel(unit);
  }

  private async load(locationId: string): Promise<void> {
    this.phase.set('loading');
    this.message.set(null);
    try {
      const response = await this.api.invoke(getLocationTariffs, { location_id: locationId });
      // Der Standort kann gewechselt haben, während die Antwort unterwegs war.
      if (this.locationId() === locationId) {
        this.tariffs.set(response.tariffs);
        this.phase.set('idle');
      }
    } catch (error) {
      this.tariffs.set(null);
      this.phase.set('error');
      this.message.set(formatApiError(error));
    }
  }
}

function toCard(tariff: LocationTariffView): TariffCard {
  const status: Status = tariff.current ? 'current' : Date.parse(tariff.validFrom) > Date.now() ? 'future' : 'past';
  return { tariff, status };
}

function unitLabel(unit: string): string {
  switch (unit?.toUpperCase()) {
    case 'CT/KWH':
      return 'ct/kWh';
    case 'EUR/MWH':
      return '€/MWh';
    default:
      return unit;
  }
}
